import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { useOrdersContext } from '../context/OrdersContext';
import { useRequestsContext } from '../context/RequestsContext';
import { useTablesContext } from '../context/TablesContext';
import { useVenueFeedback } from '../context/VenueFeedbackContext';
import { useUserPermissions, useVenueId } from '../hooks/useUserData';
import { useAuth0 } from '@auth0/auth0-react';
import history from '../utils/history';
import {
  addVenueCheckDiscount,
  addVenueCheckItems,
  addVenueCheckPayment,
  closeVenueCheck,
  openVenueTableCheck,
  updateVenueItemAvailability,
  voidVenueCheckItem,
} from '../utils/staffApiService';
import { useMenuData } from '../hooks/useMenuData';
import useItemManagement from '../hooks/useItemManagement';
import { calculateTotal, formatModifierSummary } from '../utils/itemUtils';
import '../styles/CashierPage.css';
import ModifierPickerModal from '../components/modals/ModifierPickerModal';

const ACTIVE_UNPAID_ORDER_STATUSES = new Set(['pending', 'preparing']);
const STALE_MENU_ERROR_CODES = new Set(['ITEM_NOT_ORDERABLE', 'ITEM_UNAVAILABLE']);
const AVAILABILITY_PERMISSIONS = new Set(['edit_orders', 'take_payment', 'manage_shift', 'manage_menu']);

const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} TRY`;

const paymentMethodLabel = (method) => {
  switch (method) {
    case 'cash': return 'Nakit';
    case 'card': return 'Kart';
    default: return 'Diğer';
  }
};

const getSearchTableId = (search) => {
  const params = new URLSearchParams(search || '');
  const rawTableId = params.get('tableId');
  const parsed = Number(rawTableId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getGroupedLineKey = (item) =>
  [
    item.itemId,
    toNumber(item.unitPrice),
    item.notes || '',
    JSON.stringify(item.modifiers || []),
    item.status || '',
    item.orderStatus || '',
  ].join('::');

const getLineGroupRenderKey = (item) =>
  `${item.itemId}-${(item.orderIds || []).join('-')}-${item.status || ''}-${item.notes || ''}`;

const buildGroupedLineItems = (items) => {
  const grouped = new Map();

  (items || []).forEach((item) => {
    const key = getGroupedLineKey(item);
    const current = grouped.get(key);

    if (current) {
      current.quantity += item.quantity;
      current.lineTotal += toNumber(item.lineTotal);
      current.sourceItems.push(item);
      current.orderIds.add(item.orderId);
      if (item.orderStatus) current.orderStatuses.add(item.orderStatus);
      return;
    }

    grouped.set(key, {
      ...item,
      quantity: item.quantity,
      lineTotal: toNumber(item.lineTotal),
      sourceItems: [item],
      orderIds: new Set([item.orderId]),
      orderStatuses: new Set([item.orderStatus]),
    });
  });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      orderIds: Array.from(group.orderIds).sort((left, right) => left - right),
      orderStatuses: Array.from(group.orderStatuses),
      sourceItems: [...group.sourceItems].sort((left, right) => left.orderId - right.orderId || left.id - right.id),
    }))
    .sort((left, right) => left.orderIds[0] - right.orderIds[0] || left.name.localeCompare(right.name));
};

const getGroupedLineMeta = (group) => {
  if (!group?.orderIds?.length) {
    return '';
  }

  const orderLabels = group.orderIds.map((id) => `#${id}`);
  if (group.orderIds.length === 1) {
    if ((group.sourceItems?.length || 0) > 1) {
      return `Sipariş ${orderLabels[0]} • ${group.sourceItems.length} satır birleşti`;
    }
    return `Sipariş ${orderLabels[0]}`;
  }

  if (group.orderIds.length <= 3) {
    return `Sipariş ${orderLabels.join(', ')}`;
  }

  return `${group.orderIds.length} siparişten birleştirildi`;
};

const getCashierErrorMessage = (error, fallback) => {
  switch (error?.code) {
    case 'NO_ELIGIBLE_CHECK_CONTEXT':
      return 'Bu masa için açılabilir bir adisyon bulunamadı.';
    case 'POST_PAYMENT_DISCOUNT_BLOCKED':
      return 'Ödeme başladıktan sonra indirim eklenemez.';
    case 'CHECK_PAYMENT_IN_PROGRESS':
      return 'Bu adisyonda ödeme başladı. Sadece yeni ürün eklenebilir.';
    case 'ACTIVE_ORDERS_PRESENT':
      return 'Adisyonda halen hazırlanmakta olan ürünler var.';
    case 'ITEM_NOT_ORDERABLE':
      return 'Bazı ürünler artık siparişe eklenemiyor. Menü yenilenmeli.';
    case 'ITEM_UNAVAILABLE':
      return 'Bazı ürünler artık siparişe açık değil. Menü yenilenmeli.';
    case 'BALANCE_REMAINING':
      return 'Adisyon kapatılamaz. Kalan bakiye sıfırlanmadı.';
    case 'OVERPAYMENT_NOT_ALLOWED':
      return 'Girilen ödeme kalan bakiyeyi aşıyor.';
    case 'FORBIDDEN':
      return 'Bu işlem için yetkiniz yok.';
    default:
      return error?.message || fallback;
  }
};

const getCashierExitRoute = (permissions = []) => {
  if (permissions.includes('manage_shift') || permissions.includes('edit_orders') || permissions.includes('view_orders')) {
    return '/orders';
  }
  if (permissions.includes('edit_tables') || permissions.includes('view_tables')) {
    return '/tables';
  }
  if (permissions.includes('edit_requests') || permissions.includes('view_requests')) {
    return '/requests';
  }
  return '/profile';
};

const CashierPage = () => {
  const venueId = useVenueId();
  const location = useLocation();
  const rawUserPermissions = useUserPermissions();
  const userPermissions = useMemo(() => rawUserPermissions || [], [rawUserPermissions]);
  const { getAccessTokenSilently } = useAuth0();
  const { loading: ordersLoading, refreshOrders } = useOrdersContext();
  const { loading: tablesLoading, allTables, floors } = useTablesContext();
  const { refreshRequests } = useRequestsContext();
  const { pushFeedback } = useVenueFeedback();
  const menu = useMenuData(venueId);
  const canToggleAvailability = userPermissions.some((permission) => AVAILABILITY_PERMISSIONS.has(permission));

  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [checkSummary, setCheckSummary] = useState(null);
  const [pageError, setPageError] = useState('');
  const [mutationError, setMutationError] = useState('');
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [submittingDiscount, setSubmittingDiscount] = useState(false);
  const [submittingClose, setSubmittingClose] = useState(false);
  const [submittingItems, setSubmittingItems] = useState(false);
  const [voidingItemId, setVoidingItemId] = useState(null);
  const [voidTargetItem, setVoidTargetItem] = useState(null);
  const [voidSourceGroup, setVoidSourceGroup] = useState(null);
  const [voidQuantity, setVoidQuantity] = useState('1');
  const [voidReason, setVoidReason] = useState('');
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(new Map()); // key → selected quantity
  const [lockedGroupKeys, setLockedGroupKeys] = useState(new Map());    // key → queued for current payment
  const [paidGroupKeys, setPaidGroupKeys] = useState(new Map());        // key → already paid (visual only)
  const [showDiscountTools, setShowDiscountTools] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [showPaymentNote, setShowPaymentNote] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showUnavailableItems, setShowUnavailableItems] = useState(false);
  const [composerOrderNote, setComposerOrderNote] = useState('');
  const [modifierItem, setModifierItem] = useState(null);

  const [paymentForm, setPaymentForm] = useState({
    method: 'cash',
    amount: '',
    note: '',
  });
  const [discountForm, setDiscountForm] = useState({
    type: 'fixed',
    amount: '',
    reason: '',
  });
  const emptyComposerItems = useMemo(() => [], []);

  const {
    selectedItems: composerItems,
    handleItemClick,
    handleQuantityChange,
    hasChanges: hasComposerChanges,
    resetToInitialState,
  } = useItemManagement(emptyComposerItems);

  const cashierTables = useMemo(
    () => (allTables || []).filter((table) => (table?.unpaidOrderCount || 0) > 0),
    [allTables]
  );

  const availableFloorIds = useMemo(
    () => new Set(cashierTables.map((table) => table.floor_id)),
    [cashierTables]
  );

  const visibleFloors = useMemo(
    () => (floors || []).filter((floor) => availableFloorIds.has(floor.id)),
    [availableFloorIds, floors]
  );

  const selectedTable = useMemo(
    () => cashierTables.find((table) => table.id === selectedTableId) || null,
    [cashierTables, selectedTableId]
  );

  const visibleTables = useMemo(
    () => cashierTables.filter((table) => !selectedFloorId || table.floor_id === selectedFloorId),
    [cashierTables, selectedFloorId]
  );

  const activeCategory = useMemo(
    () => menu?.categories?.find((category) => String(category.id) === String(selectedCategory)) || null,
    [menu, selectedCategory]
  );
  const availableCategoryItems = useMemo(
    () => (activeCategory?.items || []).filter((item) => item?.isAvailable !== false),
    [activeCategory]
  );
  const unavailableCategoryItems = useMemo(
    () => (activeCategory?.items || []).filter((item) => item?.isAvailable === false),
    [activeCategory]
  );

  const composerTotal = useMemo(() => calculateTotal(composerItems), [composerItems]);
  const cashierExitRoute = useMemo(() => getCashierExitRoute(userPermissions), [userPermissions]);

  useEffect(() => {
    if (visibleFloors.length === 0) {
      setSelectedFloorId(null);
      return;
    }

    if (selectedFloorId && visibleFloors.some((floor) => floor.id === selectedFloorId)) {
      return;
    }

    const queryTableId = getSearchTableId(location.search);
    const queryTable = cashierTables.find((table) => table.id === queryTableId);
    if (queryTable) {
      setSelectedFloorId(queryTable.floor_id);
      return;
    }

    setSelectedFloorId(visibleFloors[0].id);
  }, [cashierTables, location.search, selectedFloorId, visibleFloors]);

  useEffect(() => {
    const queryTableId = getSearchTableId(location.search);
    if (!queryTableId) {
      return;
    }

    const matchingTable = cashierTables.find((table) => table.id === queryTableId);
    if (!matchingTable) {
      setSelectedTableId(null);
      setCheckSummary(null);
      setPageError('Seçilen masa şu anda kasa için uygun değil.');
      setShowTablePicker(true);
      return;
    }

    setSelectedTableId(matchingTable.id);
    setSelectedFloorId(matchingTable.floor_id);
    setPageError('');
    setShowTablePicker(false);
  }, [cashierTables, location.search]);

  useEffect(() => {
    if (selectedTable) {
      return;
    }
    setCheckSummary(null);
  }, [selectedTable]);

  useEffect(() => {
    if (menu?.categories?.length) {
      if (selectedCategory && menu.categories.some((category) => String(category.id) === String(selectedCategory))) {
        return;
      }
      setSelectedCategory(menu.categories[0].id);
      return;
    }
    setSelectedCategory(null);
  }, [menu, selectedCategory]);

  useEffect(() => {
    if (!selectedTableId) {
      setShowUnavailableItems(false);
    }
    setSelectedGroupKeys(new Map());
    setLockedGroupKeys(new Map());
    setPaidGroupKeys(new Map());
  }, [selectedTableId]);

  useEffect(() => {
    if (!selectedTableId) {
      return;
    }

    let isMounted = true;
    setLoadingCheck(true);
    setMutationError('');

    openVenueTableCheck(venueId, selectedTableId, getAccessTokenSilently)
      .then((summary) => {
        if (!isMounted) return;
        setCheckSummary(summary);
        const remaining = Number(summary?.remainingBalance || 0);
        if (remaining > 0) {
          setPaymentForm(prev => ({ ...prev, amount: remaining.toFixed(2) }));
        }
        setPageError('');
      })
      .catch((error) => {
        if (!isMounted) return;
        setCheckSummary(null);
        setPageError(getCashierErrorMessage(error, 'Adisyon yüklenemedi.'));
      })
      .finally(() => {
        if (isMounted) {
          setLoadingCheck(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently, selectedTableId, venueId]);

  const paymentStarted = toNumber(checkSummary?.totalPaid) > 0;
  const remainingBalanceValue = toNumber(checkSummary?.remainingBalance);
  const normalizedPaymentAmount = paymentForm.amount.trim();
  const canSubmitPayment =
    checkSummary?.status === 'open' &&
    !submittingPayment &&
    Number.isFinite(Number(normalizedPaymentAmount)) &&
    Number(normalizedPaymentAmount) > 0;
  const canApplyDiscount = userPermissions.includes('apply_discount') && checkSummary?.status === 'open' && !paymentStarted;
  const canVoidItem = userPermissions.includes('void_order_item') && checkSummary?.status === 'open' && !paymentStarted;
  const canAddItems = checkSummary?.status === 'open';
  const hasDiscounts = (checkSummary?.discounts?.length || 0) > 0;
  const canOpenDiscountModal = hasDiscounts || canApplyDiscount;
  const discountActionLabel = hasDiscounts ? 'Detay' : canApplyDiscount ? 'Ekle' : 'Kapalı';
  const canCloseCheck = checkSummary?.status === 'open' && toNumber(checkSummary?.remainingBalance) === 0;

  const activeLineItems = useMemo(
    () =>
      (checkSummary?.orders || []).flatMap((order) =>
        (order.items || []).filter((item) => item.status !== 'cancelled').map((item) => ({
          ...item,
          orderId: order.id,
          orderStatus: order.status,
        }))
      ),
    [checkSummary]
  );

  const groupedActiveLineItems = useMemo(
    () => buildGroupedLineItems(activeLineItems),
    [activeLineItems]
  );

  const voidedLineItems = useMemo(
    () =>
      (checkSummary?.orders || []).flatMap((order) =>
        (order.items || []).filter((item) => item.status === 'cancelled').map((item) => ({
          ...item,
          orderId: order.id,
          orderStatus: order.status,
        }))
      ),
    [checkSummary]
  );

  const groupedVoidedLineItems = useMemo(
    () => buildGroupedLineItems(voidedLineItems),
    [voidedLineItems]
  );

  const splitTotal = useMemo(
    () =>
      groupedActiveLineItems.reduce((sum, item) => {
        const qty = selectedGroupKeys.get(getLineGroupRenderKey(item)) || 0;
        if (!qty) return sum;
        return sum + (item.lineTotal / item.quantity) * qty;
      }, 0),
    [groupedActiveLineItems, selectedGroupKeys]
  );

  const lockedTotal = useMemo(
    () =>
      groupedActiveLineItems.reduce((sum, item) => {
        const qty = lockedGroupKeys.get(getLineGroupRenderKey(item)) || 0;
        if (!qty) return sum;
        return sum + (item.lineTotal / item.quantity) * qty;
      }, 0),
    [groupedActiveLineItems, lockedGroupKeys]
  );

  const toggleGroupSelection = (key, availableQty) => {
    if (availableQty <= 0) return;
    setSelectedGroupKeys((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, 1);
      return next;
    });
  };

  const adjustSelectedQuantity = (key, delta, availableQty) => {
    setSelectedGroupKeys((prev) => {
      const next = new Map(prev);
      const newQty = Math.max(0, Math.min(availableQty, (next.get(key) || 0) + delta));
      if (newQty === 0) next.delete(key);
      else next.set(key, newQty);
      return next;
    });
  };

  const handleSplitFill = () => {
    setPaymentForm((prev) => ({ ...prev, amount: (lockedTotal + splitTotal).toFixed(2) }));
    setLockedGroupKeys((prev) => {
      const next = new Map(prev);
      for (const [key, qty] of selectedGroupKeys) {
        next.set(key, (next.get(key) || 0) + qty);
      }
      return next;
    });
    setSelectedGroupKeys(new Map());
  };

  const selectTable = (table) => {
    setSelectedTableId(table.id);
    setPageError('');
    setShowTablePicker(false);
    history.push(`/cashier?tableId=${table.id}`);
  };

  useEffect(() => {
    if (!checkSummary?.id) {
      setShowDiscountTools(false);
    }
  }, [checkSummary?.id]);

  useEffect(() => {
    setShowPaymentHistory(false);
  }, [checkSummary?.id]);

  useEffect(() => {
    if (!checkSummary?.id || checkSummary.status !== 'open') {
      return;
    }

    setPaymentForm((current) => {
      const nextAmount = remainingBalanceValue > 0 ? remainingBalanceValue.toFixed(2) : '';
      if (current.amount === nextAmount) {
        return current;
      }
      return {
        ...current,
        amount: nextAmount,
      };
    });
  }, [checkSummary?.id, checkSummary?.status, remainingBalanceValue]);

  const handleComposerMenuItemClick = (item) => {
    if (item?.isAvailable === false) {
      return;
    }

    if ((item.modifierGroups || []).length > 0) {
      setModifierItem(item);
      return;
    }

    handleItemClick(item);
  };

  const handleAvailabilityToggle = async (item, isAvailable, event) => {
    event?.stopPropagation();

    try {
      await updateVenueItemAvailability(venueId, item.id, isAvailable, getAccessTokenSilently);
      if (typeof menu?.refreshMenu === 'function') {
        await menu.refreshMenu();
      }
      pushFeedback({
        type: 'success',
        title: isAvailable ? 'Ürün satışa açıldı' : 'Ürün geçici olarak kapatıldı',
        message: isAvailable
          ? `${item.name} yeniden siparişe açıldı.`
          : `${item.name} artık siparişe kapalı.`,
      });
    } catch (error) {
      const message = getCashierErrorMessage(error, 'Ürünün satış durumu güncellenemedi.');
      setMutationError(message);
      pushFeedback({
        type: 'error',
        title: 'Ürün güncellenemedi',
        message,
      });
    }
  };

  const handleAddItems = async (payload) => {
    if (!checkSummary?.id) return;

    setSubmittingItems(true);
    setMutationError('');
    try {
      const summary = await addVenueCheckItems(
        venueId,
        checkSummary.id,
        {
          items: payload.items || [],
          orderNotes: payload.notes,
        },
        getAccessTokenSilently
      );
      setCheckSummary(summary);
      resetToInitialState();
      setComposerOrderNote('');
      await refreshOrders();
      pushFeedback({
        type: 'success',
        title: 'Ürünler eklendi',
        message: `${summary.tableName || selectedTable?.name || 'Masa'} adisyonu güncellendi.`,
      });
    } catch (error) {
      if (STALE_MENU_ERROR_CODES.has(error?.code) && typeof menu?.refreshMenu === 'function') {
        await menu.refreshMenu();
      }
      const message = getCashierErrorMessage(error, 'Ürünler eklenemedi.');
      setMutationError(message);
      pushFeedback({
        type: 'error',
        title: 'Ürünler eklenemedi',
        message,
      });
    } finally {
      setSubmittingItems(false);
    }
  };

  const handleSaveComposerItems = async () => {
    if (!hasComposerChanges || !composerItems.length) {
      return;
    }
    await handleAddItems({
      items: composerItems,
      notes: composerOrderNote || undefined,
    });
  };

  const handleAddDiscount = async (event) => {
    event.preventDefault();
    if (!checkSummary?.id) return;

    if (!discountForm.reason.trim()) {
      setMutationError('İndirim sebebi zorunludur.');
      return;
    }

    setSubmittingDiscount(true);
    setMutationError('');
    try {
      const summary = await addVenueCheckDiscount(
        venueId,
        checkSummary.id,
        {
          type: discountForm.type,
          amount: discountForm.amount,
          reason: discountForm.reason,
        },
        getAccessTokenSilently
      );
      setCheckSummary(summary);
      const nextRemaining = Number(summary?.remainingBalance || 0);
      if (nextRemaining > 0) {
        setPaymentForm(prev => ({ ...prev, amount: nextRemaining.toFixed(2) }));
      }
      setDiscountForm({ type: 'fixed', amount: '', reason: '' });
      setShowDiscountTools(false);
      pushFeedback({
        type: 'success',
        title: 'İndirim eklendi',
        message: 'Adisyon indirimleri güncellendi.',
      });
    } catch (error) {
      const message = getCashierErrorMessage(error, 'İndirim eklenemedi.');
      setMutationError(message);
      pushFeedback({
        type: 'error',
        title: 'İndirim eklenemedi',
        message,
      });
    } finally {
      setSubmittingDiscount(false);
    }
  };

  const handleAddPayment = async (event) => {
    event.preventDefault();
    if (!checkSummary?.id || !canSubmitPayment) return;

    setSubmittingPayment(true);
    setMutationError('');
    try {
      const summary = await addVenueCheckPayment(
        venueId,
        checkSummary.id,
        paymentForm,
        getAccessTokenSilently
      );
      setCheckSummary(summary);
      const nextRemaining = Number(summary?.remainingBalance || 0);
      setPaymentForm((current) => ({
        ...current,
        amount: nextRemaining > 0 ? nextRemaining.toFixed(2) : '',
        note: '',
      }));
      setShowPaymentNote(false);
      setShowPaymentHistory(false);
      setSelectedGroupKeys(new Map());
      setPaidGroupKeys((prev) => {
        const next = new Map(prev);
        for (const [key, qty] of lockedGroupKeys) {
          next.set(key, (next.get(key) || 0) + qty);
        }
        return next;
      });
      setLockedGroupKeys(new Map());
      pushFeedback({
        type: 'success',
        title: 'Ödeme kaydedildi',
        message: 'Ödeme adisyona işlendi.',
      });
    } catch (error) {
      const message = getCashierErrorMessage(error, 'Ödeme kaydedilemedi.');
      setMutationError(message);
      pushFeedback({
        type: 'error',
        title: 'Ödeme kaydedilemedi',
        message,
      });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const openVoidItemModal = (item) => {
    setVoidSourceGroup(null);
    setVoidTargetItem(item);
    setVoidQuantity('1');
    setVoidReason('');
    setMutationError('');
  };

  const openGroupedVoidFlow = (group) => {
    if (!group?.sourceItems?.length) {
      return;
    }

    if (group.sourceItems.length === 1) {
      openVoidItemModal(group.sourceItems[0]);
      return;
    }

    setMutationError('');
    setVoidSourceGroup(group);
  };

  const handleVoidItem = async () => {
    if (!checkSummary?.id || !voidTargetItem) return;

    const trimmedReason = voidReason.trim();
    if (!trimmedReason) {
      setMutationError('İptal sebebi zorunludur.');
      return;
    }

    const selectedQuantity = Number(voidQuantity);
    if (!Number.isFinite(selectedQuantity) || selectedQuantity < 1 || selectedQuantity > voidTargetItem.quantity) {
      setMutationError('İptal adedi geçersiz.');
      return;
    }

    setVoidingItemId(voidTargetItem.id);
    setMutationError('');
    try {
      const summary = await voidVenueCheckItem(
        venueId,
        checkSummary.id,
        voidTargetItem.id,
        { quantity: selectedQuantity, reason: trimmedReason },
        getAccessTokenSilently
      );
      setCheckSummary(summary);
      setVoidTargetItem(null);
      setVoidSourceGroup(null);
      setVoidQuantity('1');
      setVoidReason('');
      await refreshOrders();
      pushFeedback({
        type: 'success',
        title: 'Ürün iptal edildi',
        message:
          selectedQuantity === voidTargetItem.quantity
            ? 'Ürün adisyondan düşüldü.'
            : `${selectedQuantity} adet ${voidTargetItem.name} adisyondan düşüldü.`,
      });
    } catch (error) {
      const message = getCashierErrorMessage(error, 'Ürün iptal edilemedi.');
      setMutationError(message);
      pushFeedback({
        type: 'error',
        title: 'Ürün iptal edilemedi',
        message,
      });
    } finally {
      setVoidingItemId(null);
    }
  };

  const handleCloseCheck = async () => {
    if (!checkSummary?.id) return;

    setSubmittingClose(true);
    setMutationError('');
    try {
      const result = await closeVenueCheck(venueId, checkSummary.id, getAccessTokenSilently);
      await Promise.all([
        refreshOrders ? refreshOrders() : Promise.resolve(),
        refreshRequests ? refreshRequests() : Promise.resolve(),
      ]);
      setCheckSummary(null);
      setSelectedTableId(null);
      history.push('/cashier');
      pushFeedback({
        type: 'success',
        title: 'Adisyon kapatıldı',
        message:
          `Ödenen sipariş: ${result?.settledOrders ?? 0}\n` +
          `Kapatılan talep: ${result?.closedRequests ?? 0}`,
      });
    } catch (error) {
      const message = getCashierErrorMessage(error, 'Adisyon kapatılamadı.');
      setMutationError(message);
      pushFeedback({
        type: 'error',
        title: 'Adisyon kapatılamadı',
        message,
      });
    } finally {
      setSubmittingClose(false);
    }
  };

  const handleExitCashier = () => {
    setShowTablePicker(false);
    history.push(cashierExitRoute);
  };

  const paymentSubmitLabel = submittingPayment
    ? 'İşleniyor...'
    : canSubmitPayment
      ? `${toNumber(normalizedPaymentAmount).toFixed(2)} TRY ${paymentMethodLabel(paymentForm.method)} Öde`
      : 'Ödeme Ekle';

  if (ordersLoading || tablesLoading) {
    return <Loading />;
  }

  return (
    <div className="venue-page cashier-page">
      <div className="cashier-workspace">
      <div className="cashier-layout">
        <section className="venue-surface-card venue-surface-card--padded cashier-composer">
          {!selectedTableId ? (
            <div className="venue-empty-state">
              <p className="venue-empty-state__title">Masa seçin</p>
              <p className="venue-empty-state__text">Kasayı başlatmak için masa seçiciye dokunun.</p>
            </div>
          ) : (
            <div className="cashier-panel__body cashier-panel__body--composer">
              <div className="cashier-composer-workbench">
                <div className="cashier-category-nav">
                  {(menu?.categories || []).map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={`cashier-category-nav__button ${String(selectedCategory) === String(category.id) ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>

                <div className="cashier-menu-browser">
                  <div className="cashier-menu-grid">
                    {availableCategoryItems.length === 0 ? (
                      <div className="venue-empty-state">
                        <p className="venue-empty-state__title">Kategori boş</p>
                        <p className="venue-empty-state__text">Bu kategoride siparişe açık aktif ürün yok.</p>
                      </div>
                    ) : (
                      availableCategoryItems.map((item) => (
                        <div
                          key={item.id}
                          className="cashier-menu-card"
                          onClick={() => canAddItems && handleComposerMenuItemClick(item)}
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && canAddItems) {
                              event.preventDefault();
                              handleComposerMenuItemClick(item);
                            }
                          }}
                          role="button"
                          tabIndex={canAddItems ? 0 : -1}
                          aria-disabled={!canAddItems}
                          aria-label={`${item.name} ${formatCurrency(item.price)}`}
                        >
                          <span className="cashier-menu-card__name">{item.name}</span>
                          <span className="cashier-menu-card__price">{formatCurrency(item.price)}</span>
                          {canToggleAvailability ? (
                            <span className="cashier-menu-card__action-row">
                              <button
                                type="button"
                                className="cashier-menu-card__toggle"
                                onClick={(event) => handleAvailabilityToggle(item, false, event)}
                              >
                                Satışa Kapat
                              </button>
                            </span>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                  {canToggleAvailability && unavailableCategoryItems.length > 0 ? (
                    <div className="cashier-availability-panel">
                      <button
                        type="button"
                        className="cashier-availability-panel__toggle"
                        onClick={() => setShowUnavailableItems((current) => !current)}
                      >
                        Geçici Olarak Kapalı Ürünler
                        <span>{showUnavailableItems ? 'Gizle' : `${unavailableCategoryItems.length} ürün`}</span>
                      </button>
                      {showUnavailableItems ? (
                        <div className="cashier-menu-grid cashier-menu-grid--unavailable">
                          {unavailableCategoryItems.map((item) => (
                            <div
                              key={`closed-${item.id}`}
                              className="cashier-menu-card cashier-menu-card--unavailable"
                              role="group"
                            >
                              <span className="cashier-menu-card__name">{item.name}</span>
                              <span className="cashier-menu-card__price">{formatCurrency(item.price)}</span>
                              <span className="cashier-menu-card__action-row">
                                <button
                                  type="button"
                                  className="cashier-menu-card__toggle cashier-menu-card__toggle--primary"
                                  onClick={(event) => handleAvailabilityToggle(item, true, event)}
                                >
                                  Satışa Aç
                                </button>
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={`cashier-composer-tray ${composerItems.length ? 'has-items' : ''}`}>
                <div className="cashier-composer-tray__summary">
                  <div>
                    <h4 className="cashier-subtitle mb-1">Seçilen Ürünler</h4>
                    <p className="cashier-block__meta mb-0">
                      {composerItems.length > 0
                        ? `${composerItems.length} ürün satırı hazır`
                        : 'Menüden dokunarak ürün ekleyin.'}
                    </p>
                  </div>
                  <div className="cashier-composer-tray__summary-side">
                    <div className="cashier-composer-draft__total">{composerTotal} TRY</div>
                  </div>
                </div>

                {composerItems.length > 0 ? (
                  <>
                    <div className="cashier-composer-lines">
                      {composerItems.map((item) => (
                        <div key={item.lineKey || item.id} className="cashier-composer-line">
                          <div className="cashier-composer-line__main">
                            <div>
                              <div className="cashier-composer-line__title">{item.name}</div>
                              {item.modifiers?.length ? (
                                <div className="cashier-composer-line__meta">{formatModifierSummary(item.modifiers)}</div>
                              ) : null}
                              <div className="cashier-composer-line__meta">x{item.quantity}</div>
                            </div>
                            <span className="cashier-composer-line__price">{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          <div className="cashier-composer-line__side">
                            <Button
                              variant="outline-secondary"
                              className="cashier-composer-line__action"
                              aria-label={`${item.name} azalt`}
                              onClick={() => handleQuantityChange(item.lineKey, -1)}
                            >
                              -1
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Form.Group controlId="cashierComposerNote" className="cashier-composer-draft__note">
                      <Form.Label>Adisyon notu</Form.Label>
                      <Form.Control
                        value={composerOrderNote}
                        onChange={(event) => setComposerOrderNote(event.target.value)}
                        placeholder="Opsiyonel"
                        maxLength={500}
                      />
                    </Form.Group>

                    <Button
                      variant="primary"
                      className="cashier-composer-draft__submit"
                      disabled={!canAddItems || !hasComposerChanges || !composerItems.length || submittingItems}
                      onClick={handleSaveComposerItems}
                    >
                      {submittingItems ? 'Ekleniyor...' : 'Seçili Ürünleri Ekle'}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </section>

        <section className="venue-surface-card venue-surface-card--padded cashier-main">
          {!selectedTableId ? (
            <div className="venue-empty-state">
              <p className="venue-empty-state__title">Masa seçin</p>
              <p className="venue-empty-state__text">Kasada işlem yapmak için soldan bir masa seçin.</p>
              <Button variant="outline-primary" className="mt-2 align-self-center" onClick={() => setShowTablePicker(true)}>
                Masa Seç
              </Button>
            </div>
          ) : loadingCheck ? (
            <div className="cashier-loading">
              <Spinner animation="border" />
            </div>
          ) : pageError ? (
            <Alert variant="warning" className="mb-0">{pageError}</Alert>
          ) : checkSummary ? (
            <>
              {mutationError ? (
                <Alert variant="danger">{mutationError}</Alert>
              ) : null}

              <div className="cashier-grid">
                <div className="cashier-panel cashier-panel--lines">
                  <div className="cashier-panel__body cashier-panel__body--lines">
                    <div className="cashier-line-stack">
                      {activeLineItems.length === 0 ? (
                        <div className="venue-empty-state">
                          <p className="venue-empty-state__title">Aktif ürün yok</p>
                          <p className="venue-empty-state__text">Bu adisyonda gösterilecek aktif satır bulunmuyor.</p>
                        </div>
                      ) : (
                        <div className="cashier-line-list">
                          {groupedActiveLineItems.map((item) => {
                            const renderKey = getLineGroupRenderKey(item);
                            const lockedQty = lockedGroupKeys.get(renderKey) || 0;
                            const paidQty = paidGroupKeys.get(renderKey) || 0;
                            const availableQty = item.quantity - lockedQty - paidQty;
                            const isFullyLocked = availableQty <= 0;
                            const selectedQty = selectedGroupKeys.get(renderKey) || 0;
                            const isSelected = selectedQty > 0;
                            const unitPrice = item.quantity > 0 ? item.lineTotal / item.quantity : 0;
                            return (
                              <div
                                key={renderKey}
                                className={`cashier-line-item${isFullyLocked ? ' cashier-line-item--locked' : ' cashier-line-item--selectable'}${isSelected ? ' cashier-line-item--selected' : ''}`}
                                onClick={isFullyLocked ? undefined : () => toggleGroupSelection(renderKey, availableQty)}
                                role={isFullyLocked ? undefined : 'checkbox'}
                                aria-checked={isFullyLocked ? undefined : isSelected}
                                tabIndex={isFullyLocked ? -1 : 0}
                                onKeyDown={isFullyLocked ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroupSelection(renderKey, availableQty); } }}
                              >
                                <span
                                  className={`cashier-split-check${isFullyLocked ? ' cashier-split-check--locked' : isSelected ? ' cashier-split-check--checked' : ''}`}
                                  aria-hidden="true"
                                />
                                <div>
                                  <div className="cashier-line-item__title">{item.quantity}x {item.name}</div>
                                  <div className="cashier-line-item__meta">
                                    {getGroupedLineMeta(item)}
                                    {(lockedQty + paidQty) > 0 && !isFullyLocked ? (
                                      <span className="cashier-split-partial-badge">{lockedQty + paidQty}/{item.quantity} aktarıldı</span>
                                    ) : null}
                                    {item.orderStatuses && item.orderStatuses.some((status) => ACTIVE_UNPAID_ORDER_STATUSES.has(status)) ? (
                                      <span className="cashier-line-item__status-chip cashier-line-item__status-chip--active">Hazırlanıyor</span>
                                    ) : null}
                                  </div>
                                  {item.modifiers?.length ? (
                                    <div className="cashier-line-item__note">{formatModifierSummary(item.modifiers)}</div>
                                  ) : null}
                                  {item.notes ? <div className="cashier-line-item__note">{item.notes}</div> : null}
                                </div>
                                <div className="cashier-line-item__side">
                                  {isSelected && availableQty > 1 ? (
                                    <div className="cashier-split-stepper" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        className="cashier-split-stepper__btn"
                                        onClick={() => adjustSelectedQuantity(renderKey, -1, availableQty)}
                                      >−</button>
                                      <span className="cashier-split-stepper__qty">{selectedQty}/{availableQty}</span>
                                      <button
                                        type="button"
                                        className="cashier-split-stepper__btn"
                                        disabled={selectedQty >= availableQty}
                                        onClick={() => adjustSelectedQuantity(renderKey, +1, availableQty)}
                                      >+</button>
                                    </div>
                                  ) : (
                                    <div className="cashier-line-item__price">{formatCurrency(isSelected ? unitPrice * selectedQty : item.lineTotal)}</div>
                                  )}
                                  {canVoidItem && !isSelected && !isFullyLocked ? (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      disabled={voidingItemId !== null}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openGroupedVoidFlow(item);
                                      }}
                                    >
                                      {voidingItemId !== null ? 'İptal...' : item.quantity > 1 ? 'Adet İptal Et' : 'İptal Et'}
                                    </Button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {voidedLineItems.length > 0 ? (
                        <div className="cashier-voided-list">
                          <h4 className="cashier-subtitle">İptal edilen ürünler</h4>
                          {groupedVoidedLineItems.map((item) => (
                            <div key={`${item.itemId}-${item.orderIds.join('-')}-${item.status}-${item.notes || ''}`} className="cashier-line-item cashier-line-item--voided">
                              <div>
                                <div className="cashier-line-item__title">{item.quantity}x {item.name}</div>
                                <div className="cashier-line-item__meta">{getGroupedLineMeta(item)}</div>
                                {item.modifiers?.length ? (
                                  <div className="cashier-line-item__note">{formatModifierSummary(item.modifiers)}</div>
                                ) : null}
                              </div>
                              <div className="cashier-line-item__price">{formatCurrency(item.lineTotal)}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="cashier-block cashier-block--totals">
                      {(selectedGroupKeys.size > 0 || lockedGroupKeys.size > 0) && (
                        <div className="cashier-split-bar cashier-split-bar--active">
                          <div className="cashier-split-bar__body">
                            {lockedGroupKeys.size > 0 && (
                              <div className="cashier-split-bar__locked">
                                <span className="cashier-split-bar__locked-label">Aktarıldı:</span>
                                <span className="cashier-split-bar__locked-amount">{formatCurrency(lockedTotal)}</span>
                                <button
                                  type="button"
                                  className="cashier-split-bar__fix"
                                  onClick={() => setLockedGroupKeys(new Map())}
                                >
                                  Düzelt
                                </button>
                              </div>
                            )}
                            {selectedGroupKeys.size > 0 && (
                              <>
                                <div className="cashier-split-bar__info">
                                  <span>{[...selectedGroupKeys.values()].reduce((a, b) => a + b, 0)} adet</span>
                                  <strong>{formatCurrency(splitTotal)}</strong>
                                </div>
                                <button
                                  type="button"
                                  className="cashier-split-bar__fill"
                                  onClick={handleSplitFill}
                                >
                                  Ödemeye Aktar →
                                </button>
                              </>
                            )}
                          </div>
                          {selectedGroupKeys.size > 0 && (
                            <button
                              type="button"
                              className="cashier-split-bar__exit"
                              onClick={() => setSelectedGroupKeys(new Map())}
                              title="Seçimi temizle"
                              aria-label="Seçimi temizle"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      )}
                      <div className={`cashier-balance-card ${
                        toNumber(checkSummary.remainingBalance) > 0 ? 'cashier-balance-card--due' : 'cashier-balance-card--zero'
                      }`}>
                        <div className="cashier-balance-card__topline">
                          <span className="cashier-balance-card__eyebrow">Kalan</span>
                          <span className="cashier-balance-card__amount">{formatCurrency(checkSummary.remainingBalance)}</span>
                        </div>
                        <div className="cashier-summary-grid">
                          <div className="cashier-summary-chip">
                            <span className="cashier-summary-chip__label">Ara toplam</span>
                            <strong>{formatCurrency(checkSummary.subtotal)}</strong>
                          </div>
                          <div className="cashier-summary-chip">
                            <span className="cashier-summary-chip__label">İndirim</span>
                            <strong>{formatCurrency(checkSummary.discountTotal)}</strong>
                          </div>
                          <div className="cashier-summary-chip">
                            <span className="cashier-summary-chip__label">Ödenen</span>
                            <strong>{formatCurrency(checkSummary.totalPaid)}</strong>
                          </div>
                          <div className="cashier-summary-chip">
                            <span className="cashier-summary-chip__label">Toplam</span>
                            <strong>{formatCurrency(checkSummary.grandTotal)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cashier-panel cashier-panel--actions">
                  <div className="cashier-panel__body cashier-panel__body--summary">
                    <button
                      type="button"
                      className="cashier-context-mini"
                      onClick={() => setShowTablePicker(true)}
                    >
                      <div className="cashier-context-mini__content">
                        <div className="cashier-context-mini__table">{selectedTable?.name || checkSummary?.tableName}</div>
                        <div className="cashier-context-mini__meta">
                          {checkSummary?.id ? `Adisyon #${checkSummary.id}` : 'Adisyon bekleniyor'}
                          {groupedActiveLineItems.length ? ` • ${groupedActiveLineItems.length} satır` : ''}
                        </div>
                      </div>
                    </button>
                    <div className="cashier-block cashier-block--payments">
                      <Form onSubmit={handleAddPayment} className="cashier-form cashier-form--payment">
                        <Form.Group controlId="cashierPaymentMethod" className="cashier-form__wide">
                          <div className="cashier-method-toggle" role="group" aria-label="Ödeme yöntemi">
                            {[
                              ['cash', 'Nakit'],
                              ['card', 'Kart'],
                              ['other', 'Diğer'],
                            ].map(([methodValue, methodLabel]) => (
                              <Button
                                key={methodValue}
                                type="button"
                                className="cashier-method-toggle__button"
                                variant={paymentForm.method === methodValue ? 'success' : 'outline-secondary'}
                                disabled={checkSummary.status !== 'open' || submittingPayment}
                                onClick={() => setPaymentForm((current) => ({ ...current, method: methodValue }))}
                              >
                                {methodLabel}
                              </Button>
                            ))}
                          </div>
                        </Form.Group>
                        <Form.Group controlId="cashierPaymentAmount">
                          <div className="cashier-form__label-row">
                            <Form.Label className="cashier-form__label">Tutar</Form.Label>
                            {showPaymentNote || paymentForm.note ? (
                              <Button
                                type="button"
                                variant="outline-secondary"
                                className="cashier-inline-toggle cashier-inline-toggle--amount"
                                disabled={submittingPayment}
                                onClick={() => {
                                  setPaymentForm((current) => ({ ...current, note: '' }));
                                  setShowPaymentNote(false);
                                }}
                              >
                                Notu kaldır
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="outline-secondary"
                                className="cashier-inline-toggle cashier-inline-toggle--amount"
                                disabled={submittingPayment}
                                onClick={() => setShowPaymentNote(true)}
                              >
                                Not ekle
                              </Button>
                            )}
                          </div>
                          <Form.Control
                            value={paymentForm.amount}
                            onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))}
                            placeholder="Örn. 120.50"
                            inputMode="decimal"
                            aria-label="Ödeme tutarı"
                            disabled={checkSummary.status !== 'open' || submittingPayment}
                          />
                        </Form.Group>
                        {showPaymentNote || paymentForm.note ? (
                          <Form.Group controlId="cashierPaymentNote" className="cashier-form__wide">
                            <Form.Label className="cashier-form__label">Not</Form.Label>
                            <Form.Control
                              value={paymentForm.note}
                              onChange={(event) => setPaymentForm((current) => ({ ...current, note: event.target.value }))}
                              placeholder="Opsiyonel"
                              disabled={checkSummary.status !== 'open' || submittingPayment}
                            />
                          </Form.Group>
                        ) : null}
                        <Button
                          type="submit"
                          variant="success"
                          className="cashier-form__wide"
                          disabled={!canSubmitPayment}
                        >
                          {paymentSubmitLabel}
                        </Button>
                      </Form>

                      {checkSummary.payments?.length ? (
                        <div className="cashier-payment-history">
                          <button
                            type="button"
                            className="cashier-payment-history__toggle"
                            aria-expanded={showPaymentHistory}
                            onClick={() => setShowPaymentHistory((current) => !current)}
                          >
                            <span className="cashier-payment-history__count">
                              {checkSummary.payments.length} ödeme • {formatCurrency(checkSummary.totalPaid)}
                            </span>
                            <span className="cashier-payment-history__chevron">{showPaymentHistory ? '▲' : '▼'}</span>
                          </button>
                          {showPaymentHistory ? (
                            <div className="cashier-list cashier-list--payments">
                              {checkSummary.payments.map((payment) => (
                                <div key={payment.id} className="cashier-list__item">
                                  <span>{paymentMethodLabel(payment.method)}</span>
                                  <span>{formatCurrency(payment.amount)}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="cashier-block cashier-block--discounts">
                      <div className="cashier-block__header">
                        <div>
                          <h4 className="cashier-subtitle mb-1">İndirimler</h4>
                          <p className="cashier-block__meta mb-0">
                            {hasDiscounts ? `${checkSummary.discounts.length} indirim kaydı var` : 'Henüz indirim yok.'}
                          </p>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={!canOpenDiscountModal}
                          onClick={() => setShowDiscountTools((current) => !current)}
                        >
                          {discountActionLabel}
                        </Button>
                      </div>
                    </div>

                    <div className="cashier-block cashier-block--close">
                      <Button variant="dark" disabled={!canCloseCheck || submittingClose} onClick={handleCloseCheck}>
                        {submittingClose ? 'Kapatılıyor...' : 'Adisyonu Kapat'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </section>
      </div>

      <Modal
        show={showTablePicker}
        onHide={() => setShowTablePicker(false)}
        centered
        size="xl"
        dialogClassName="cashier-picker-modal"
        backdrop
        keyboard
      >
        <Modal.Header closeButton>
          <div className="cashier-picker-modal__header">
            <div className="venue-page-eyebrow">Kasa</div>
            <h2 className="venue-section-title mb-1">Kasa Masaları</h2>
            <div className="table-orders-modal__subtitle">İşlem yapılacak masayı seçin.</div>
          </div>
        </Modal.Header>
        <Modal.Body className="cashier-picker-modal__body">
          {visibleFloors.length > 0 ? (
            <div className="venue-floor-switcher cashier-floor-switcher">
              {visibleFloors.map((floor) => (
                <button
                  key={floor.id}
                  type="button"
                  className={`venue-floor-switcher__button ${selectedFloorId === floor.id ? 'active' : ''}`}
                  onClick={() => setSelectedFloorId(floor.id)}
                >
                  {floor.name}
                </button>
              ))}
            </div>
          ) : null}

          {visibleTables.length === 0 ? (
            <div className="venue-empty-state">
              <p className="venue-empty-state__title">Açık adisyon yok</p>
              <p className="venue-empty-state__text">Bu katta kasaya uygun açık masa bulunmuyor.</p>
            </div>
          ) : (
            <div className="cashier-table-list">
              {visibleTables.map((table) => (
                <button
                  key={table.id}
                  type="button"
                  className={`cashier-table-list__item ${selectedTableId === table.id ? 'active' : ''}`}
                  onClick={() => selectTable(table)}
                >
                  <span className="cashier-table-list__name">{table.name}</span>
                  <span className="cashier-table-list__meta">{table.unpaidOrderCount} açık sipariş</span>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
        {!selectedTableId ? (
          <Modal.Footer>
            <div className="venue-modal-actions">
              <Button variant="outline-secondary" onClick={handleExitCashier}>
                Çık
              </Button>
            </div>
          </Modal.Footer>
        ) : null}
      </Modal>

      <Modal
        show={showDiscountTools}
        onHide={submittingDiscount ? undefined : () => setShowDiscountTools(false)}
        centered
      >
        <Modal.Header closeButton={!submittingDiscount}>
          <Modal.Title>İndirimler</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hasDiscounts ? (
            <div className="cashier-list mb-3">
              {checkSummary?.discounts?.map((discount) => (
                <div key={discount.id} className="cashier-list__item">
                  <span>{discount.type === 'percentage' ? `%${discount.amount}` : formatCurrency(discount.amount)}</span>
                  <span>{discount.reason || 'Nedensiz indirim'}</span>
                </div>
              ))}
            </div>
          ) : null}

          {canApplyDiscount ? (
            <Form onSubmit={handleAddDiscount} className="cashier-form cashier-form--discount">
              <Form.Group controlId="cashierDiscountType">
                <Form.Label>İndirim tipi</Form.Label>
                <Form.Control
                  as="select"
                  value={discountForm.type}
                  onChange={(event) => setDiscountForm((current) => ({ ...current, type: event.target.value }))}
                  disabled={submittingDiscount}
                >
                  <option value="fixed">Tutar</option>
                  <option value="percentage">Yüzde</option>
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="cashierDiscountAmount">
                <Form.Label>İndirim tutarı</Form.Label>
                <Form.Control
                  value={discountForm.amount}
                  onChange={(event) => setDiscountForm((current) => ({ ...current, amount: event.target.value }))}
                  placeholder="Örn. 25"
                  disabled={submittingDiscount}
                />
              </Form.Group>
              <Form.Group controlId="cashierDiscountReason" className="cashier-form__wide">
                <Form.Label>Açıklama</Form.Label>
                <Form.Control
                  value={discountForm.reason}
                  onChange={(event) => setDiscountForm((current) => ({ ...current, reason: event.target.value }))}
                  placeholder="Zorunlu"
                  disabled={submittingDiscount}
                />
              </Form.Group>
              <Button
                type="submit"
                variant="outline-dark"
                className="cashier-form__wide"
                disabled={submittingDiscount || !discountForm.reason.trim()}
              >
                {submittingDiscount ? 'Ekleniyor...' : 'İndirim Ekle'}
              </Button>
            </Form>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(voidSourceGroup)}
        onHide={voidingItemId ? undefined : () => setVoidSourceGroup(null)}
        centered
      >
        <Modal.Header closeButton={!voidingItemId}>
          <Modal.Title>İptal edilecek satırı seçin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {voidSourceGroup ? (
            <div className="cashier-source-list">
              {voidSourceGroup.sourceItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="cashier-source-list__item"
                  disabled={Boolean(voidingItemId)}
                  onClick={() => openVoidItemModal(item)}
                >
                  <div>
                    <div className="cashier-line-item__title">{item.quantity}x {item.name}</div>
                    <div className="cashier-line-item__meta">Sipariş #{item.orderId}</div>
                    {item.notes ? <div className="cashier-line-item__note">{item.notes}</div> : null}
                  </div>
                  <div className="cashier-line-item__price">{formatCurrency(item.lineTotal)}</div>
                </button>
              ))}
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(voidTargetItem)}
        onHide={voidingItemId ? undefined : () => setVoidTargetItem(null)}
        centered
        dialogClassName="venue-confirm-modal"
      >
        <Modal.Header closeButton={!voidingItemId}>
          <Modal.Title>Ürün iptal et</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {voidTargetItem ? (
            <>
              <p className="mb-3">
                <strong>{voidTargetItem.name}</strong> ürününden kaç adet iptal edilsin?
              </p>
              <Form.Group controlId="cashierVoidQuantity">
                <Form.Label>İptal adedi</Form.Label>
                <Form.Control
                  as="select"
                  value={voidQuantity}
                  disabled={Boolean(voidingItemId)}
                  onChange={(event) => setVoidQuantity(event.target.value)}
                >
                  {Array.from({ length: voidTargetItem.quantity }, (_, index) => index + 1).map((count) => (
                    <option key={count} value={count}>
                      {count} adet
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="cashierVoidReason" className="mt-3">
                <Form.Label>İptal sebebi</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={voidReason}
                  disabled={Boolean(voidingItemId)}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="İptal nedenini yazın..."
                />
              </Form.Group>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <div className="venue-modal-actions">
            <Button variant="outline-secondary" onClick={() => setVoidTargetItem(null)} disabled={Boolean(voidingItemId)}>
              Vazgeç
            </Button>
            <div className="venue-modal-actions__spacer" />
            <Button variant="danger" onClick={handleVoidItem} disabled={Boolean(voidingItemId) || !voidReason.trim()}>
              {voidingItemId ? 'İptal ediliyor...' : 'İptal Et'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <ModifierPickerModal
        show={Boolean(modifierItem)}
        item={modifierItem}
        onClose={() => setModifierItem(null)}
        onConfirm={(configuredItem) => {
          handleItemClick(configuredItem);
          setModifierItem(null);
        }}
        title="Ürünü Yapılandır"
      />
      </div>
    </div>
  );
};

export default CashierPage;
